import CloseIcon from "@mui/icons-material/Close";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import SearchIcon from "@mui/icons-material/Search";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import {
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
} from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";
import { VendorTicketDetail } from "../../../@types/DRO/WarrantyTicketsResponse";
import { WarrantyTicketsApi } from "../../../api/WarrantyTickets/WarrantyTicketsApi";
import { MEDIA_BASE_URL } from "../../../config/apiConfig";
import { useLoading } from "../../../views/Loader/LoadingContext";

const STATUS_STYLES: Record<string, React.CSSProperties> = {
  repaired: { backgroundColor: "#E2FFF6", color: "#00BC84" },
  replaced: { backgroundColor: "#E9F2FF", color: "#1366D9" },
  rejected: { backgroundColor: "#FFF0EE", color: "#F94932" },
};

const ROWS_PER_PAGE = 10;

const ActionHistory: React.FC = () => {
  const { setLoading } = useLoading();
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [page, setPage] = useState(0);
  const [tickets, setTickets] = useState<VendorTicketDetail[]>([]);
  const [hasNext, setHasNext] = useState(false);
  const [pictureRow, setPictureRow] = useState<VendorTicketDetail | null>(null);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const fetchHistory = useCallback(async () => {
    setLoading(true);
    try {
      const response = await WarrantyTicketsApi.api.getWarrantyTicketsHistory(
        page,
        ROWS_PER_PAGE,
        searchQuery,
        statusFilter,
        fromDate,
        toDate
      );
      setTickets(response.data.vendorTicketDetails ?? []);
      setHasNext(response.data.hasNext ?? false);
    } catch (error) {
      console.error("Error fetching action history", error);
      setTickets([]);
    }
    setLoading(false);
  }, [page, searchQuery, statusFilter, fromDate, toDate]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  const handleStatusChange = (e: SelectChangeEvent) => {
    setStatusFilter(e.target.value);
    setPage(0);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    setPage(0);
  };

  const filtered = tickets;

  return (
    <>
    <div className="flex-1 overflow-auto bg-white p-6">
        <h1 className="text-3xl font-semibold text-gray-800 mb-5 text-left">
          Action History
        </h1>

        {/* Filter bar */}
        <div className="flex items-center gap-3 mb-5 flex-wrap">
          <TextField
            size="small"
            placeholder="Search by UID"
            value={searchQuery}
            onChange={handleSearchChange}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <SearchIcon fontSize="small" className="text-gray-400" />
                </InputAdornment>
              ),
            }}
            sx={{ width: 200, bgcolor: "white" }}
          />

          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-500 font-medium">Status :</span>
            <Select
              size="small"
              value={statusFilter}
              onChange={handleStatusChange}
              sx={{ minWidth: 100, bgcolor: "white", fontSize: 14 }}>
              <MenuItem value="All">All</MenuItem>
              <MenuItem value="REPAIRED">Repaired</MenuItem>
              <MenuItem value="REPLACED">Replaced</MenuItem>
              <MenuItem value="REJECTED">Rejected</MenuItem>
              <MenuItem value="CLOSED">Closed</MenuItem>
            </Select>
          </div>

          <div className="ml-auto flex items-center gap-2">
            <TextField
              type="date"
              size="small"
              label="From"
              value={fromDate}
              onChange={(e) => { setFromDate(e.target.value); setPage(0); }}
              InputLabelProps={{ shrink: true }}
              inputProps={{ max: new Date().toISOString().split("T")[0] }}
              sx={{ width: 150, bgcolor: "white" }}
            />
            <TextField
              type="date"
              size="small"
              label="To"
              value={toDate}
              onChange={(e) => { setToDate(e.target.value); setPage(0); }}
              InputLabelProps={{ shrink: true }}
              inputProps={{ max: new Date().toISOString().split("T")[0] }}
              sx={{ width: 150, bgcolor: "white" }}
            />
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left">
              <thead>
                <tr className="border-b border-gray-100">
                  {[
                    "Hub Name",
                    "OEM Name",
                    "Part Name",
                    "UID No.",
                    "Onboarding Date",
                    "Old Part's Picture",
                    "New UID No.",
                    "Action Status",
                  ].map((col) => (
                    <th
                      key={col}
                      className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">
                      {col}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((row, index) => (
                  <tr
                    key={index}
                    className="border-b border-gray-50 hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3 text-gray-700 whitespace-nowrap">
                      {row.hubName}
                    </td>
                    <td className="px-4 py-3 text-gray-700 whitespace-nowrap">
                      {row.oemName}
                    </td>
                    <td className="px-4 py-3 text-gray-700 whitespace-nowrap">
                      {row.componentName ?? "-"}
                    </td>
                    <td className="px-4 py-3 text-gray-700 whitespace-nowrap">
                      {row.uid}
                    </td>
                    <td className="px-4 py-3 text-gray-700 whitespace-nowrap">
                      {row.onboardingDate}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<VisibilityOutlinedIcon fontSize="small" />}
                        onClick={() => row.imageId && setPictureRow(row)}
                        sx={{
                          textTransform: "none",
                          fontSize: 12,
                          borderColor: "#2563EB",
                          color: "#2563EB",
                          "&:hover": {
                            borderColor: "#1D4ED8",
                            bgcolor: "#EFF6FF",
                          },
                        }}>
                        View Picture
                      </Button>
                    </td>
                    <td className="px-4 py-3 text-gray-700 whitespace-nowrap">
                      {row.newUid ?? (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span
                        className="inline-block px-2.5 py-0.5 rounded text-xs font-semibold"
                        style={
                          STATUS_STYLES[row.state?.toLowerCase() ?? ""] ?? {
                            backgroundColor: "#F3F4F6",
                            color: "#6B7280",
                          }
                        }>
                        {row.state}
                      </span>
                    </td>
                  </tr>
                ))}
                {filtered.length === 0 && (
                  <tr>
                    <td
                      colSpan={8}
                      className="px-4 py-10 text-center text-gray-400">
                      No records found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-end gap-1 px-4 py-3 border-t border-gray-100">
            <button
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0}
              className="w-7 h-7 flex items-center justify-center rounded text-gray-500 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed text-sm">
              ‹
            </button>
            <span className="text-sm text-gray-600 px-2">Page {page + 1}</span>
            <button
              onClick={() => setPage((p) => p + 1)}
              disabled={!hasNext}
              className="w-7 h-7 flex items-center justify-center rounded text-gray-500 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed text-sm">
              ›
            </button>
          </div>
        </div>
    </div>

      {/* Image Preview Modal */}
      <Dialog
        open={!!pictureRow}
        onClose={() => setPictureRow(null)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle
          sx={{
            fontWeight: 600,
            fontSize: 16,
            color: "#1F2937",
            pb: 1,
          }}>
          {/* Image Name */}
          <IconButton
            onClick={() => setPictureRow(null)}
            sx={{ position: "absolute", right: 0, top: -2, color: "#6B7280" }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pb: 3, px: 3 }}>
          <div
            className="flex items-center justify-center rounded-lg"
            style={{
              border: "1.5px dashed #D1D5DB",
              minHeight: 220,
              background: "#FAFAFA",
            }}>
            {pictureRow?.imageId ? (
              <img
                src={`${MEDIA_BASE_URL}/${pictureRow.imageId}`}
                alt="Part"
                style={{ maxHeight: 280, maxWidth: "100%", objectFit: "contain", borderRadius: 8 }}
                onError={(e) => {
                  (e.currentTarget as HTMLImageElement).style.display = "none";
                  (e.currentTarget.nextSibling as HTMLElement).style.display = "flex";
                }}
              />
            ) : null}
            <div
              className="flex flex-col items-center justify-center text-gray-300 gap-2"
              style={{ display: pictureRow?.imageId ? "none" : "flex" }}>
              <ImageOutlinedIcon sx={{ fontSize: 48, color: "#D1D5DB" }} />
              <span className="text-sm text-gray-400">Image Preview</span>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ActionHistory;